import {Component} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

/**
 * crosshair
 */
export class Crosshair extends Component {
    static TypeName = 'crosshair';

    /* Properties that are configurable in the editor */
    @property.float(1.0)
    param!: number;

    _overlay: HTMLDivElement | null = null;
    _crosshair: HTMLDivElement | null = null;
    _arrow: HTMLDivElement | null = null;
    _text: HTMLDivElement | null = null;
    _isMobile: boolean = false;
    _styleElement: HTMLStyleElement | null = null;
    _onPointerLockChange = () => {};
    _onPointerLockError = () => {};

    onActivate() {
        this._isMobile = /Mobi|Android/i.test(navigator.userAgent);
        if (this._isMobile) return;

        this._loadFont().then(() => {
            this._createStyles();
            this._createOverlay();
            this._attachPointerLockListeners();
        });
    }

    update(dt: number) {
        // no per-frame logic
    }

    onDeactivate() {
        // exit pointer lock if active
        if (document.pointerLockElement === document.querySelector('canvas')) {
            document.exitPointerLock();
        }

        document.removeEventListener('pointerlockchange', this._onPointerLockChange);
        document.removeEventListener('pointerlockerror', this._onPointerLockError);

        if (this._overlay && this._overlay.parentElement) {
            this._overlay.parentElement.removeChild(this._overlay);
            this._overlay = null;
        }
        if (this._styleElement && this._styleElement.parentElement) {
            this._styleElement.parentElement.removeChild(this._styleElement);
            this._styleElement = null;
        }
    }

    async _loadFont() {
        const font = new FontFace('BRLNSB', 'url(BRLNSB.TTF)');
        await font.load();
        document.fonts.add(font);
        await document.fonts.ready;
    }

    _createStyles() {
        this._styleElement = document.createElement('style');
        this._styleElement.innerHTML = `
            @font-face {
                font-family: 'BRLNSB';
                src: url('BRLNSB.TTF');
            }
            .wl-crosshair-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                backdrop-filter: blur(12px);
                background: rgba(0, 0, 0, 0.5);
                z-index: 9999;
            }
            .wl-crosshair-text {
                position: absolute;
                left: 50%;
                top: calc(50% - 100px); /* raised above arrow */
                transform: translateX(-50%);
                color: rgb(255, 255, 255);
                font-size: 1.4rem;
                font-weight: bold;
                font-family: 'BRLNSB', sans-serif;
                padding: 0.5rem 1rem;
                background: rgba(255, 255, 255, 0.2);
                backdrop-filter: blur(6px);
                border-radius: 8px;
                text-align: center;
            }
            .wl-crosshair-arrow {
                position: absolute;
                left: 50%;
                top: calc(50% - 30px); /* arrow below text */
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 12px solid transparent;
                border-right: 12px solid transparent;
                border-top: 16px solid rgb(255, 255, 255);
                animation: arrow-bounce 1.2s infinite;
            }
            .wl-crosshair-dot {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                width: 24px;
                height: 24px;
                border: 3px solid rgb(255, 255, 255);
                border-radius: 50%;
                cursor: pointer;
                transition: transform 0.2s ease;
            }
            .wl-crosshair-dot:hover {
                transform: translate(-50%, -50%) scale(1.2);
            }
            @keyframes arrow-bounce {
                0%, 100% { transform: translateX(-50%) translateY(0); }
                50% { transform: translateX(-50%) translateY(-10px); }
            }
        `;
        document.head.appendChild(this._styleElement);
    }

    _createOverlay() {
        this._overlay = document.createElement('div');
        this._overlay.className = 'wl-crosshair-overlay';

        // Instruction text
        this._text = document.createElement('div');
        this._text.className = 'wl-crosshair-text';
        this._text.innerText = 'Click to lock & play';

        // Animated arrow
        this._arrow = document.createElement('div');
        this._arrow.className = 'wl-crosshair-arrow';

        // Center dot
        this._crosshair = document.createElement('div');
        this._crosshair.className = 'wl-crosshair-dot';

        // Append in order: text, arrow, dot
        [this._text, this._arrow, this._crosshair].forEach((el) => {
            if (el) this._overlay!.appendChild(el);
        });

        document.body.appendChild(this._overlay);

        // If canvas is already pointer-locked, unlock so user can click overlay
        const canvas = document.querySelector('canvas');
        if (canvas && document.pointerLockElement === canvas) {
            document.exitPointerLock();
        }

        this._crosshair.addEventListener('click', () => {
            const cv = document.querySelector('canvas');
            if (!cv) return;
            // enter fullscreen
            if (cv.requestFullscreen) {
                cv.requestFullscreen();
            } else if ((cv as any).webkitRequestFullscreen) {
                (cv as any).webkitRequestFullscreen(); // Safari
            }
            cv.requestPointerLock();
        });
    }

    _attachPointerLockListeners() {
        this._onPointerLockChange = () => {
            const isLocked =
                document.pointerLockElement === document.querySelector('canvas');
            if (this._overlay) this._overlay.style.display = isLocked ? 'none' : 'block';
        };
        this._onPointerLockError = (e) => {
            console.error('Pointer lock error:', e);
        };
        document.addEventListener('pointerlockchange', this._onPointerLockChange);
        document.addEventListener('pointerlockerror', this._onPointerLockError);
    }
}
